import { countAppliedFilters } from "../../filters-redesign/js/global";

/**
 * Calculate YYYYMMDD date boundaries for relative date horizons (Pattern B)
 */
export const calculateDateHorizon = (rangeType) => {
    const formatYYYYMMDD = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}${m}${day}`;
    };

    if (rangeType === "30d") {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return { ge: formatYYYYMMDD(d) };
    } else if (rangeType === "6m") {
        const d = new Date();
        d.setMonth(d.getMonth() - 6);
        return { ge: formatYYYYMMDD(d) };
    } else if (rangeType === "12m" || rangeType === "1y") {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return { ge: formatYYYYMMDD(d) };
    } else if (rangeType === "older12m" || rangeType === "pre2024") {
        const d = new Date();
        d.setFullYear(d.getFullYear() - 1);
        return { le: formatYYYYMMDD(d) };
    } else if (/^\d{4}$/.test(rangeType)) {
        return { ge: `${rangeType}0101`, le: `${rangeType}1231` };
    }
    return null; // 'all'
};

/**
 * Pattern B: Relative Horizons Date Dropdown Handler
 */
export const initDateHorizonDropdown = () => {
    const dateContainers = document.querySelectorAll(".dropdown-list.date-horizon, [data-date-container]");
    if (!dateContainers.length) return;

    dateContainers.forEach((container) => {
        if (container._dateHorizonBound) return;
        container._dateHorizonBound = true;

        const fbjsVarName = container.getAttribute("data-fbjs-variable-name") || "searchNewsLanding";
        const narrowingVarName = container.getAttribute("data-fbjs-narrowing-variable-name") || "fbdate";
        const dropdownParent = container.closest(".c-filters__dropdown-select");
        const titleBtn = dropdownParent?.querySelector(".c-filters__dropdown-title");
        const defaultTitle = dropdownParent?.getAttribute("data-title") || "Date";

        // Setup ARIA radiogroup & radio options
        const ul = container.querySelector("ul");
        if (ul) {
            ul.setAttribute("role", "radiogroup");
            ul.setAttribute("aria-label", "Date range");
        }
        container.querySelectorAll("li[data-date-range]").forEach((li) => {
            li.setAttribute("role", "radio");
            li.setAttribute("tabindex", "0");
            const isActive = li.classList.contains("dropdownActive");
            li.setAttribute("aria-checked", isActive ? "true" : "false");
        });

        const selectDateOption = (li) => {
            const rangeType = li.getAttribute("data-date-range");
            const labelText = li.querySelector(".facet-label")?.innerText?.trim() || li.innerText.trim();

            // 1. Update active item & ARIA checked in list
            container.querySelectorAll("li").forEach((item) => {
                item.classList.remove("dropdownActive");
                item.setAttribute("aria-checked", "false");
            });
            li.classList.add("dropdownActive");
            li.setAttribute("aria-checked", "true");

            // 2. Update button label and active class
            if (titleBtn) {
                if (rangeType === "all") {
                    titleBtn.innerText = defaultTitle;
                    dropdownParent?.classList.remove("dropdownActive");
                } else {
                    titleBtn.innerText = labelText;
                    dropdownParent?.classList.add("dropdownActive");
                }
            }

            // 3. Update active facet chip in .active-facets
            const activeFacets = document.querySelector(".c-filters .active-facets");
            if (activeFacets) {
                const existingChip = activeFacets.querySelector('span[param="date"]');
                if (existingChip) existingChip.remove();

                if (rangeType !== "all") {
                    const chip = document.createElement("span");
                    chip.setAttribute("param", "date");
                    chip.setAttribute("name", labelText);
                    chip.innerText = `${defaultTitle}: ${labelText}`;
                    activeFacets.appendChild(chip);
                }
            }

            // 4. Update Funnelback parameters & trigger search
            const searchInstance = window[fbjsVarName];
            if (searchInstance && searchInstance.search) {
                if (!searchInstance.search.additionalFBParams) {
                    searchInstance.search.additionalFBParams = {};
                }
                const geKey = `ge_${narrowingVarName}`;
                const leKey = `le_${narrowingVarName}`;

                delete searchInstance.search.additionalFBParams[geKey];
                delete searchInstance.search.additionalFBParams[leKey];

                const bounds = calculateDateHorizon(rangeType);
                if (bounds) {
                    if (bounds.ge) searchInstance.search.additionalFBParams[geKey] = bounds.ge;
                    if (bounds.le) searchInstance.search.additionalFBParams[leKey] = bounds.le;
                }

                searchInstance.search.startRank = 1;
                if (typeof searchInstance.doSearch === "function") {
                    searchInstance.doSearch(true, true);
                }
            }

            // 5. Close dropdown gracefully
            if (dropdownParent) {
                dropdownParent.classList.remove("dropdown-list-focus");
                const btn = dropdownParent.querySelector(".js-accordion-trigger");
                if (btn) btn.setAttribute("aria-expanded", "false");
                container.hidden = true;
            }

            if (typeof countAppliedFilters === "function") {
                countAppliedFilters();
            }
        };

        // EVENT: Click selection
        container.addEventListener("click", (e) => {
            const li = e.target.closest("li[data-date-range]");
            if (li) selectDateOption(li);
        });

        // EVENT: Keyboard navigation (Enter, Space, ArrowUp, ArrowDown)
        container.addEventListener("keydown", (e) => {
            const items = Array.from(container.querySelectorAll("li[data-date-range]"));
            const currentLi = document.activeElement?.closest("li[data-date-range]");
            if (!currentLi) return;
            const currentIndex = items.indexOf(currentLi);

            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selectDateOption(currentLi);
                titleBtn?.focus();
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                const nextIndex = (currentIndex + 1) % items.length;
                items[nextIndex].focus();
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                const prevIndex = (currentIndex - 1 + items.length) % items.length;
                items[prevIndex].focus();
            }
        });
    });
};

/**
 * Legacy Fallback: Twin Input Handler (for backwards compatibility)
 */
const initLegacyDateInputs = () => {
    const dateFromSelector = document.querySelector(".fbjs-date-from[type='text'], .fbjs-date-from[type='date']");
    const dateToSelector = document.querySelector(".fbjs-date-to[type='text'], .fbjs-date-to[type='date']");
    if (!dateFromSelector && !dateToSelector) return;

    const dateSwitcher = (target, metadata) => {
        let dataValue = target.value || "";
        const fbjsVariableName = target.getAttribute("data-fbjs-variable-name");
        if (!window[fbjsVariableName] || !window[fbjsVariableName].search) return;

        if (!window[fbjsVariableName].search.additionalFBParams) {
            window[fbjsVariableName].search.additionalFBParams = {};
        }

        if (dataValue) {
            dataValue = dataValue.replace(/-/g, "");
            window[fbjsVariableName].search.additionalFBParams[metadata] = dataValue;
            window[fbjsVariableName].doSearch(true, true);
        } else {
            if (window[fbjsVariableName].search.additionalFBParams[metadata]) {
                delete window[fbjsVariableName].search.additionalFBParams[metadata];
                window[fbjsVariableName].doSearch(true, true);
            }
        }
    };

    if (dateFromSelector && !dateFromSelector._boundLegacy) {
        dateFromSelector._boundLegacy = true;
        dateFromSelector.addEventListener("change", (e) => {
            const narrowingVar = dateFromSelector.getAttribute("data-fbjs-narrowing-variable-name") || "fbdate";
            dateSwitcher(e.currentTarget, `ge_${narrowingVar}`);
        });
    }

    if (dateToSelector && !dateToSelector._boundLegacy) {
        dateToSelector._boundLegacy = true;
        dateToSelector.addEventListener("change", (e) => {
            const narrowingVar = dateToSelector.getAttribute("data-fbjs-narrowing-variable-name") || "fbdate";
            dateSwitcher(e.currentTarget, `le_${narrowingVar}`);
        });
    }
};

/**
 * Main export: initializes Pattern B relative horizons dropdown, with legacy fallback
 */
export const fbjsDateNarrowing = () => {
    initDateHorizonDropdown();
    initLegacyDateInputs();
};
